import { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, GitCommit } from 'lucide-react';
import { useGetRdShareCommits, getGetRdShareCommitsQueryKey, type GetRdShareCommitsBucket } from '@edison/api-client-react';
import { SlidePanel } from '@/components/ui/slide-panel.js';
import { Segmented } from '@/components/ui/tabs-ds.js';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { SkeletonRow } from '@/components/ui/skeleton.js';
import { InlineError } from '@/components/ui/inline-error.js';

type BucketFilter = 'all' | 'core' | 'supporting' | 'out';

const BUCKET_META: Record<'core' | 'supporting' | 'out', { variant: 'positive' | 'info' | 'default'; label: string }> = {
  core: { variant: 'positive', label: 'Core' },
  supporting: { variant: 'info', label: 'Supporting' },
  out: { variant: 'default', label: 'Out' },
};

interface Props {
  open: boolean;
  onClose: () => void;
  projectId: string | null;
  projectName: string;
  incomeYear: string;
}

/**
 * Per-commit drill-down behind a project's R&D share (Task 8's GET /api/rd-shares/:projectId/commits).
 * Pagination is entirely server-side (the `page` param round-trips to a real SQL LIMIT/OFFSET) —
 * the full corpus is never fetched and sliced client-side, per the brief.
 */
export function CommitDrilldownPanel({ open, onClose, projectId, projectName, incomeYear }: Props) {
  const [bucket, setBucket] = useState<BucketFilter>('all');
  const [page, setPage] = useState(1);
  const [expandedSha, setExpandedSha] = useState<string | null>(null);

  // Reset filter/page/expansion state whenever a different project's panel is opened.
  useEffect(() => {
    if (open) {
      setBucket('all');
      setPage(1);
      setExpandedSha(null);
    }
  }, [open, projectId]);

  const bucketParam = bucket === 'all' ? undefined : (bucket as GetRdShareCommitsBucket);

  const params = { incomeYear, bucket: bucketParam, page };
  const { data, isLoading, error, refetch } = useGetRdShareCommits(
    projectId ?? '',
    params,
    { query: { enabled: open && !!projectId, queryKey: getGetRdShareCommitsQueryKey(projectId ?? '', params) } },
  );

  const items = data?.data?.items ?? [];
  const pagination = data?.data?.pagination;

  return (
    <SlidePanel open={open} onClose={onClose} title={`Commits — ${projectName}`} width="w-[620px]">
      <div className="space-y-4 p-5">
        <Segmented<BucketFilter>
          options={[
            { value: 'all', label: 'All' },
            { value: 'core', label: 'Core' },
            { value: 'supporting', label: 'Supporting' },
            { value: 'out', label: 'Out' },
          ]}
          value={bucket}
          onChange={(v) => { setBucket(v); setPage(1); }}
        />

        {isLoading && (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)}
          </div>
        )}

        {!isLoading && !!error && (
          <InlineError message="Failed to load commits." onRetry={() => void refetch()} />
        )}

        {!isLoading && !error && items.length === 0 && (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No classified commits yet — run the classification batch.
          </p>
        )}

        {!isLoading && !error && items.length > 0 && (
          <ul className="space-y-1.5">
            {items.map((c) => {
              const isOpen = expandedSha === c.sha;
              const bucketMeta = c.bucket ? BUCKET_META[c.bucket] : null;
              return (
                <li key={c.sha} className="rounded-lg border px-3 py-2 text-sm">
                  <button
                    type="button"
                    className="flex w-full items-start gap-2 text-left"
                    aria-expanded={isOpen}
                    onClick={() => setExpandedSha(isOpen ? null : c.sha)}
                  >
                    {isOpen ? (
                      <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                    ) : (
                      <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                    )}
                    <GitCommit className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="font-mono text-xs text-muted-foreground">{c.sha.slice(0, 7)}</span>
                        {bucketMeta ? (
                          <Badge variant={bucketMeta.variant}>{bucketMeta.label}</Badge>
                        ) : (
                          <Badge variant="default">Unclassified</Badge>
                        )}
                      </span>
                      <span className="mt-0.5 block truncate text-sm text-foreground">{c.message}</span>
                    </span>
                  </button>
                  {isOpen && (
                    <div className="ml-6 mt-2 space-y-1 text-xs text-muted-foreground">
                      {c.confidence != null && <p>Confidence: {c.confidence}</p>}
                      <p>{c.rationale ?? 'No rationale recorded.'}</p>
                      {c.committedAt && <p>{new Date(c.committedAt).toLocaleString('en-AU')}</p>}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {pagination && (pagination.totalPages ?? 0) > 1 && (
          <div className="flex items-center justify-between pt-2">
            <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              Previous
            </Button>
            <span className="text-xs text-muted-foreground">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <Button
              variant="secondary"
              size="sm"
              disabled={page >= (pagination.totalPages ?? 1)}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </SlidePanel>
  );
}
